import React, { useRef, useState, useEffect , useContext} from "react";
import mapboxgl from "mapbox-gl";
import { TextField, Box, Button, List, ListItem, ListItemButton, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import { 
    Satellite as SatelliteIcon,
    MapOutlined as MapOutlinedIcon,
    Map as MapIcon,
    Terrain as TerrainIcon2,
    NightsStay as NightsStayIcon,
    WbSunny as WbSunnyIcon
} from "@mui/icons-material";
import { MAP_BOX_API_KEY } from "../services/AppConstant";

import { GlobalContext } from "../GlobalProvider";
mapboxgl.accessToken = MAP_BOX_API_KEY;

// Map style options
const mapStyles = [
    {
        id: 'satellite',
        name: 'Satellite',
        url: 'mapbox://styles/mapbox/satellite-v9',
        icon: <SatelliteIcon />,
        description: 'High-resolution satellite imagery'
    },
    {
        id: 'satellite-streets',
        name: 'Satellite Streets',
        url: 'mapbox://styles/mapbox/satellite-streets-v12',
        icon: <MapOutlinedIcon />,
        description: 'Satellite with street labels'
    },
    {
        id: 'streets',
        name: 'Streets',
        url: 'mapbox://styles/mapbox/streets-v12',
        icon: <MapIcon />,
        description: 'Standard street map'
    },
    {
        id: 'outdoors',
        name: 'Outdoors',
        url: 'mapbox://styles/mapbox/outdoors-v12',
        icon: <TerrainIcon2 />,
        description: 'Outdoor recreation map'
    },
    {
        id: 'light',
        name: 'Light',
        url: 'mapbox://styles/mapbox/light-v11',
        icon: <WbSunnyIcon />,
        description: 'Light theme street map'
    },
    {
        id: 'dark',
        name: 'Dark',
        url: 'mapbox://styles/mapbox/dark-v11',
        icon: <NightsStayIcon />,
        description: 'Dark theme street map'
    },
    {
        id: 'navigation',
        name: 'Navigation',
        url: 'mapbox://styles/mapbox/navigation-day-v1',
        icon: <MapIcon />,
        description: 'Navigation optimized map'
    },
    {
        id: 'navigation-night',
        name: 'Navigation Night',
        url: 'mapbox://styles/mapbox/navigation-night-v1',
        icon: <NightsStayIcon />,
        description: 'Night navigation map'
    }
];

const LocationSelector = ({ initialLocation = { lat: 0, lng: 0 }, onLocationSelect }) => {
    const mapContainerRef = useRef(null);

    const { fullApp } = useContext(GlobalContext);
    const [address, setAddress] = useState("");
    const [currentMapStyle, setCurrentMapStyle] = useState('streets');
    
    const isValidLocation = (location) => {
        return location && 
               typeof location.lat === "number" && 
               typeof location.lng === "number" && 
               !isNaN(location.lat) && 
               !isNaN(location.lng) && 
               location.lat !== 0 && 
               location.lng !== 0;
    };

    const defaultLocation = isValidLocation(initialLocation) 
        ? initialLocation 
        : fullApp.geolocation || { lat: 0, lng: 0 };
    const [location, setLocation] = useState(defaultLocation);
    const [suggestions, setSuggestions] = useState([]);
    const [mapInstance, setMapInstance] = useState(null);
    const [markerInstance, setMarkerInstance] = useState(null);

    // Get current map style URL
    const getCurrentMapStyle = () => {
        const style = mapStyles.find(s => s.id === currentMapStyle);
        return style ? style.url : mapStyles[0].url;
    };

    useEffect(() => {
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: getCurrentMapStyle(),
            center: [location.lng, location.lat],
            zoom: 12,
        });

        const marker = new mapboxgl.Marker({ draggable: true })
            .setLngLat([location.lng, location.lat])
            .addTo(map);

        marker.on("dragend", () => {
            const { lng, lat } = marker.getLngLat();
            setLocation({ lat, lng });
        });

        setMapInstance(map);
        setMarkerInstance(marker);

        return () => map.remove();
    }, []);

    // Update map style when currentMapStyle changes
    useEffect(() => {
        if (mapInstance) {
            mapInstance.setStyle(getCurrentMapStyle());
            
            // Re-add marker after style change
            mapInstance.on('style.load', () => {
                if (markerInstance) {
                    markerInstance.addTo(mapInstance);
                }
            });
        }
    }, [currentMapStyle, mapInstance]);

    

    useEffect(() => {
        if (mapInstance && markerInstance) {
            mapInstance.setCenter([location.lng, location.lat]);
            markerInstance.setLngLat([location.lng, location.lat]);
        }
    }, [location, mapInstance, markerInstance]);

    const fetchSuggestions = async (query) => {
        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
                    query
                )}.json?access_token=${MAP_BOX_API_KEY}&autocomplete=true&limit=5`
            );
            const data = await response.json();
            setSuggestions(data.features || []);
        } catch (error) {
            console.error("Error fetching suggestions:", error);
        }
    };

    const handleAddressChange = (e) => {
        const value = e.target.value;
        setAddress(value);

        if (value.trim()) {
            fetchSuggestions(value);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        const { center, place_name } = suggestion;
        setLocation({ lng: center[0], lat: center[1] });
        setAddress(place_name);
        setSuggestions([]);
    };

    const handleConfirm = () => {
        onLocationSelect(location.lat, location.lng, address);
    };

    const handleMapStyleChange = (event) => {
        setCurrentMapStyle(event.target.value);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                    label="Search Address"
                    fullWidth
                    value={address}
                    onChange={handleAddressChange}
                />
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Map Style</InputLabel>
                    <Select
                        value={currentMapStyle}
                        label="Map Style"
                        onChange={handleMapStyleChange}
                    >
                        {mapStyles.map((style) => (
                            <MenuItem key={style.id} value={style.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {style.icon}
                                    <Box>
                                        <Typography variant="body2">{style.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {style.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <List
                sx={{
                    position: "absolute",
                    zIndex: 1000,
                    backgroundColor: "white",
                    width: "100%",
                    maxHeight: "200px",
                    overflow: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    mb: 2,
                }}
            >
                {suggestions.map((suggestion) => (
                    <ListItem key={suggestion.id} disablePadding>
                        <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                            {suggestion.place_name}
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Box
                ref={mapContainerRef}
                sx={{
                    height: "400px",
                    width: "100%",
                    mb: 2,
                }}
            />
            <Button variant="contained" onClick={handleConfirm}>
                Confirm Location
            </Button>
        </Box>
    );
};

export default LocationSelector;
